import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { LoadingSpinner } from '../../../components/layout/SharedComponents';
import { Badge } from '../../../components/ui/badge';
import { Tag, Calendar, CheckCircle, Printer } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

export default function VersionHistoryPanel() {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/system/version')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handlePrint = () => {
    const currentVersion = data?.version || '?';
    const historyData = data?.history || [];
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Eyvo eProcurement - Version History</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #1a1a1a; }
          .header { border-bottom: 2px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { font-size: 28px; font-weight: 700; color: #1a1a1a; }
          .header p { font-size: 14px; color: #6b7280; margin-top: 4px; }
          .meta { display: flex; gap: 20px; margin-top: 12px; font-size: 12px; color: #6b7280; }
          .current-version { background: #eff6ff; border: 2px solid #2563eb; border-radius: 12px; padding: 20px; margin-bottom: 30px; }
          .current-version .label { font-size: 11px; font-weight: 600; color: #2563eb; text-transform: uppercase; letter-spacing: 1px; }
          .current-version .version { font-size: 32px; font-weight: 700; color: #2563eb; margin-top: 4px; }
          .current-version .date { font-size: 12px; color: #6b7280; margin-top: 4px; }
          .release { border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 16px; page-break-inside: avoid; }
          .release-header { background: #f9fafb; padding: 12px 16px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
          .release-version { background: #2563eb; color: white; padding: 4px 10px; border-radius: 6px; font-size: 13px; font-weight: 600; font-family: monospace; }
          .release-current { background: #10b981; }
          .release-date { font-size: 12px; color: #6b7280; }
          .release-body { padding: 16px; }
          .release-body ul { list-style: none; }
          .release-body li { font-size: 13px; color: #374151; padding: 6px 0; padding-left: 20px; position: relative; }
          .release-body li:before { content: "✓"; position: absolute; left: 0; color: #10b981; font-weight: bold; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 12px; color: #6b7280; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Eyvo eProcurement</h1>
          <p>Version History & Release Notes</p>
          <div class="meta">
            <span>Current: v${currentVersion}</span>
            <span>Printed: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz })}</span>
          </div>
        </div>
        
        <div class="current-version">
          <div class="label">Current Version</div>
          <div class="version">v${currentVersion}</div>
          <div class="date">Deployed: ${data?.deployed_at ? new Date(data.deployed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz }) : '—'}</div>
        </div>
        
        ${historyData.map((v, i) => `
          <div class="release">
            <div class="release-header">
              <span class="release-version ${i === 0 ? 'release-current' : ''}">v${v.version}${i === 0 ? ' (Current)' : ''}</span>
              <span class="release-date">${v.date ? new Date(v.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', timeZone: tz }) : '—'}</span>
            </div>
            <div class="release-body">
              <ul>
                ${(v.changes || []).map(c => `<li>${c}</li>`).join('')}
              </ul>
            </div>
          </div>
        `).join('')}
        
        <div class="footer">
          <p>&copy; ${new Date().getFullYear()} Eyvo Inc. All rights reserved.</p>
          <p>www.eyvo.com | support@eyvo.com</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  if (loading) return <LoadingSpinner />;
  if (!data) return <p className="text-sm text-muted-foreground">Version data unavailable.</p>;

  return (
    <div className="space-y-6" data-testid="version-history-panel">
      {/* Header with Print Button */}
      <div className="flex items-center justify-between">
        <div />
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          data-testid="print-version-history-btn"
        >
          <Printer className="h-4 w-4" />
          Print History
        </button>
      </div>

      {/* Current Version */}
      <div className="border rounded-xl p-5 bg-primary/5 border-primary/20">
        <div className="flex items-center gap-3 mb-2">
          <Tag className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold text-primary">v{data.version}</h2>
          <Badge className="bg-green-100 text-green-700 border-green-200">Current</Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Deployed: {data.deployed_at ? new Date(data.deployed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '—'}
        </p>
      </div>

      {/* Version Timeline */}
      <div>
        <h3 className="text-sm font-bold mb-3">Release History</h3>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />

          <div className="space-y-4">
            {(data.history || []).map((release, i) => (
              <div key={release.version} className="relative flex gap-4">
                {/* Timeline dot */}
                <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  i === 0 ? 'bg-primary text-white' : 'bg-muted border border-border'
                }`}>
                  {i === 0 ? <CheckCircle className="h-3.5 w-3.5" /> : <div className="w-2 h-2 rounded-full bg-muted-foreground/40" />}
                </div>

                {/* Release content */}
                <div className={`flex-1 border rounded-lg p-4 ${i === 0 ? 'border-primary/20 bg-card' : 'bg-card'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-bold font-mono text-primary">v{release.version}</span>
                    <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {release.date ? new Date(release.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : ''}
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {(release.changes || []).map((change, j) => (
                      <li key={j} className="text-xs text-muted-foreground flex items-start gap-2">
                        <span className="text-primary mt-0.5 flex-shrink-0">-</span>
                        <span>{change}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
